import { useEffect, useCallback, useRef } from 'react';

interface WebVitalsMetric {
  name: 'LCP' | 'FID' | 'CLS' | 'FCP' | 'TTFB' | 'INP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

interface WebVitalsThresholds {
  LCP: { good: number; poor: number };
  FID: { good: number; poor: number };
  CLS: { good: number; poor: number };
  FCP: { good: number; poor: number };
  TTFB: { good: number; poor: number };
  INP: { good: number; poor: number };
}

const THRESHOLDS: WebVitalsThresholds = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
};

function getRating(name: keyof WebVitalsThresholds, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function generateId(): string {
  return `v${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useWebVitals(onReport?: (metric: WebVitalsMetric) => void) {
  const reportedRef = useRef<Set<string>>(new Set());
  
  const report = useCallback((metric: WebVitalsMetric) => {
    if (reportedRef.current.has(metric.name)) return;
    reportedRef.current.add(metric.name);
    
    if (process.env.NODE_ENV === 'development') {
      const color = metric.rating === 'good' ? '#0cce6b' : 
                    metric.rating === 'needs-improvement' ? '#ffa400' : '#ff4e42';
      console.log(
        `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)}`,
        `color: ${color}; font-weight: bold;`
      );
    }
    
    onReport?.(metric);
  }, [onReport]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    const navigationType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type || 'navigate';

    // Largest Contentful Paint (LCP)
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
        
        report({
          name: 'LCP',
          value: lastEntry.startTime,
          rating: getRating('LCP', lastEntry.startTime),
          delta: lastEntry.startTime,
          id: generateId(),
          navigationType,
        });
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {}

    // First Input Delay (FID)
    try {
      const fidObserver = new PerformanceObserver((entryList) => {
        const firstEntry = entryList.getEntries()[0] as PerformanceEventTiming;
        const value = firstEntry.processingStart - firstEntry.startTime;
        
        report({
          name: 'FID',
          value,
          rating: getRating('FID', value),
          delta: value,
          id: generateId(),
          navigationType,
        });
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {}

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    let clsEntries: PerformanceEntry[] = [];
    
    try {
      const clsObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries() as (PerformanceEntry & { hadRecentInput: boolean; value: number })[]) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            clsEntries.push(entry);
          }
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      
      // Report CLS on page hide
      const reportCLS = () => {
        report({
          name: 'CLS',
          value: clsValue,
          rating: getRating('CLS', clsValue),
          delta: clsValue,
          id: generateId(),
          navigationType,
        });
      };
      
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          reportCLS();
        }
      });
    } catch (e) {}

    // First Contentful Paint (FCP)
    try {
      const fcpObserver = new PerformanceObserver((entryList) => {
        const fcpEntry = entryList.getEntries().find(e => e.name === 'first-contentful-paint');
        if (fcpEntry) {
          report({
            name: 'FCP',
            value: fcpEntry.startTime,
            rating: getRating('FCP', fcpEntry.startTime),
            delta: fcpEntry.startTime,
            id: generateId(),
            navigationType,
          });
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
    } catch (e) {}

    // Time to First Byte (TTFB)
    try {
      const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navEntry) {
        const ttfb = navEntry.responseStart - navEntry.requestStart;
        report({
          name: 'TTFB',
          value: ttfb,
          rating: getRating('TTFB', ttfb),
          delta: ttfb,
          id: generateId(),
          navigationType,
        });
      }
    } catch (e) {}

    // Interaction to Next Paint (INP)
    try {
      let maxINP = 0;
      const inpObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries() as PerformanceEventTiming[]) {
          const duration = entry.duration;
          if (duration > maxINP) {
            maxINP = duration;
          }
        }
      });
      inpObserver.observe({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit);
      
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && maxINP > 0) {
          report({
            name: 'INP',
            value: maxINP,
            rating: getRating('INP', maxINP),
            delta: maxINP,
            id: generateId(),
            navigationType,
          });
        }
      });
    } catch (e) {}

  }, [report]);
}

export function WebVitalsMonitor() {
  useWebVitals((metric) => {
    // In production, you could send this to an analytics endpoint
    if (process.env.NODE_ENV === 'production') {
      // Example: send to analytics
      // fetch('/api/analytics/vitals', {
      //   method: 'POST',
      //   body: JSON.stringify(metric),
      //   headers: { 'Content-Type': 'application/json' },
      // }).catch(() => {});
    }
  });
  
  return null;
}
